import React, { useState, useRef, useEffect } from 'react'
import { Slider } from 'antd'
import moment from 'moment'

import './MapPlaybackControl.css'

const formatTimeOnly = (ts) => (ts ? moment(ts).format('HH:mm') : '–')

const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 3, 4, 5]

const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M8 5v14l11-7-11-7z" fill="rgba(31,35,40,.85)" />
  </svg>
)

const PauseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M7 5h4v14H7V5zm6 0h4v14h-4V5z" fill="rgba(31,35,40,.85)" />
  </svg>
)

const ChevronUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M7 14l5-5 5 5" stroke="rgba(31,35,40,.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M7 10l5 5 5-5" stroke="rgba(31,35,40,.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ReplayIcon = () => (
  <svg width="14px" height="14px" viewBox="0 0 0.84 0.84" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.368 0.42V0.315l0.088 0.053 0.088 0.053 -0.088 0.053 -0.088 0.053z" fill="#000000" stroke="#000000" stroke-width="0.07" stroke-linejoin="round" />
    <path d="M0.197 0.643A0.31 0.31 0 0 0 0.42 0.735 0.315 0.315 0 1 0 0.197 0.197L0.105 0.298" stroke="#000000" stroke-width="0.07" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M0.105 0.158v0.14h0.14" stroke="#000000" stroke-width="0.07" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
)

const StepBackwardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="4.5" y="5" width="2.5" height="14" rx="1" fill="rgba(31,35,40,.85)" />
    <path d="M18 5v14L9 12l9-7z" fill="rgba(31,35,40,.85)" />
  </svg>
)

const StepForwardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M6 5v14l9-7-9-7z" fill="rgba(31,35,40,.85)" />
    <rect x="17" y="5" width="2.5" height="14" rx="1" fill="rgba(31,35,40,.85)" />
  </svg>
)

function MapPlaybackControl(props) {
  const {
    visible,
    fleetRangeData,
    fleetTimeRange,
    onTimeRangeChange,
    filteredFleetRangeData,
    currentIndex,
    formatRangeTime,
    playbackSpeed,
    onSpeedChange,
    isPlaying,
    onPlayPause,
    onReplay,
    onStep,
    onStepBack
  } = props

  const [panelExpanded, setPanelExpanded] = useState(false)
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false)
  const [positionTop, setPositionTop] = useState(false)
  const controlRef = useRef(null)

  useEffect(() => {
    if (!speedMenuOpen) return
    const el = controlRef.current
    function handleClickOutside(e) {
      if (el && e.target && !el.contains(e.target)) {
        setSpeedMenuOpen(false)
      }
    }
    const id = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true)
    }, 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [speedMenuOpen])

  if (!visible || !fleetRangeData || !fleetRangeData.length) return null

  const startTs = filteredFleetRangeData[0] && filteredFleetRangeData[0].timestamp
  const endTs = filteredFleetRangeData[filteredFleetRangeData.length - 1] && filteredFleetRangeData[filteredFleetRangeData.length - 1].timestamp
  const nowTs = filteredFleetRangeData[currentIndex] && filteredFleetRangeData[currentIndex].timestamp

  const pillTimeText = [formatTimeOnly(startTs), formatTimeOnly(endTs)].filter(Boolean).join(' – ') || '–'
  const nowLabel = formatTimeOnly(nowTs)

  const timeRangeSliderMax = Math.max(fleetRangeData.length - 1, 0)

  return (
    <div className={`map-playback-controls ${positionTop ? 'map-playback-controls--top' : ''}`} ref={controlRef}>
      {/* Collapsed pill */}
      <div className="pb-pill pb-ui">
        <button type="button" className="pb-icon-btn" onClick={onReplay} title="Back to start" aria-label="Back to start">
          <ReplayIcon />
        </button>
        <button type="button" className="pb-icon-btn" onClick={onStepBack} title="Step back one point" aria-label="Step back">
          <StepBackwardIcon />
        </button>
        <button type="button" className="pb-icon-btn" onClick={onPlayPause} title={isPlaying ? 'Pause' : 'Play'} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button type="button" className="pb-icon-btn" onClick={onStep} title="Step forward one point" aria-label="Step forward">
          <StepForwardIcon />
        </button>
        <span className="pb-time-text">{pillTimeText}</span>
        <div className="pb-divider" />
        <div className="pb-dropdown">
          <button type="button" className="pb-speed-btn" onClick={(e) => { e.stopPropagation(); setSpeedMenuOpen(!speedMenuOpen) }} title="Speed" aria-label="Speed">
            ⚡ <span>{playbackSpeed}x</span> <span style={{ color: 'rgba(31,35,40,.35)' }}>▾</span>
          </button>
          <div className={`pb-menu ${speedMenuOpen ? 'open' : ''}`} role="menu" aria-label="Speed options">
            {SPEED_OPTIONS.map((s) => (
              <button key={s} type="button" data-speed={s} onClick={(e) => { e.stopPropagation(); onSpeedChange(s); setSpeedMenuOpen(false) }}>
                {s}x
              </button>
            ))}
          </div>
        </div>
        <div className="pb-divider" />
        <span className="pb-muted">🕒 {nowLabel}</span>
        <button type="button" className="pb-toggle" onClick={(e) => { e.stopPropagation(); setPanelExpanded(v => !v) }} title={panelExpanded ? 'Collapse' : 'Expand'} aria-label={panelExpanded ? 'Collapse' : 'Expand'}>
          {panelExpanded ? <ChevronUp /> : <ChevronDown />}
        </button>
        <div className="pb-divider" />
        <div className="pb-seg pb-position-seg" aria-label="Position">
          <button type="button" className={positionTop ? 'active' : ''} onClick={(e) => { e.stopPropagation(); setPositionTop(true) }}>Top</button>
          <button type="button" className={!positionTop ? 'active' : ''} onClick={(e) => { e.stopPropagation(); setPositionTop(false) }}>Bottom</button>
        </div>
      </div>

      {/* Expanded panel */}
      <div className={`pb-panel pb-ui ${panelExpanded ? 'open' : ''}`} style={{ display: panelExpanded ? 'block' : 'none' }}>
        <div className="pb-panel-inner">
          <button type="button" className="pb-icon-btn" onClick={onReplay} title="Back to start" aria-label="Back to start">
            <ReplayIcon />
          </button>
          <button type="button" className="pb-icon-btn" onClick={onStepBack} title="Step back one point" aria-label="Step back">
            <StepBackwardIcon />
          </button>
          <button type="button" className="pb-icon-btn" onClick={onPlayPause} title={isPlaying ? 'Pause' : 'Play'} aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button type="button" className="pb-icon-btn" onClick={onStep} title="Step forward one point" aria-label="Step forward">
            <StepForwardIcon />
          </button>
          <div className="pb-slider-wrap">
            <span className="pb-muted">{formatRangeTime(fleetRangeData[0] && fleetRangeData[0].timestamp)}</span>
            <Slider
              range
              min={0}
              max={timeRangeSliderMax}
              value={fleetTimeRange}
              step={1}
              tipFormatter={(idx) => formatRangeTime(fleetRangeData[idx] && fleetRangeData[idx].timestamp)}
              onChange={(nextRange) => {
                if (!nextRange || nextRange.length !== 2) return
                onTimeRangeChange(nextRange)
              }}
            />
            <span className="pb-muted">{formatRangeTime(fleetRangeData[fleetRangeData.length - 1] && fleetRangeData[fleetRangeData.length - 1].timestamp)}</span>
          </div>
          <div className="pb-seg" aria-label="Speed">
            {SPEED_OPTIONS.map((s) => (
              <button key={s} type="button" className={playbackSpeed === s ? 'active' : ''} data-speed={s} onClick={() => onSpeedChange(s)}>
                {s}x
              </button>
            ))}
          </div>
          <button type="button" className="pb-toggle" onClick={(e) => { e.stopPropagation(); setPanelExpanded(false) }} title="Close" aria-label="Close">
            <ChevronUp />
          </button>
        </div>
      </div>
    </div>
  )
}

export default MapPlaybackControl
